import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

// ═══ Types ═══

export type ImpactoOperacional = 'bloqueia_medicao' | 'alerta_planejamento' | 'informativo';

export interface ConformidadeResultado {
  id: string;
  contrato_id: string;
  os_id: string | null;
  planilha_versao_id: string | null;
  cronograma_versao_id: string | null;
  regra: string;
  severidade: 'error' | 'warning' | 'info';
  entidade_afetada: string;
  descricao: string;
  sugestao_correcao: string | null;
  dados_contexto: Record<string, unknown> | null;
  created_at: string;
}

export interface ConformidadeExecucao {
  id: string;
  contrato_id: string;
  os_id: string | null;
  total_erros: number;
  total_avisos: number;
  total_info: number;
  score_conformidade: number | null;
  trigger_type: string;
  created_at: string;
}

export interface ConformidadeThresholds {
  x3_limite_ponderacao: number;
  score_peso_error: number;
  score_peso_warning: number;
}

export interface ConformidadeGuardaChuva {
  contrato_id: string;
  total_os_ativas: number;
  os_validadas: number;
  score_ponderado: number | null;
  total_erros: number;
  total_avisos: number;
  total_info: number;
  ultima_validacao: string | null;
  os_breakdown: Array<{
    os_id: string;
    numero_os: number;
    descricao: string;
    bac: number;
    score: number | null;
    erros: number;
    avisos: number;
    validada: boolean;
  }>;
}

// ═══ Derived helpers ═══

const DEFAULT_THRESHOLDS: ConformidadeThresholds = {
  x3_limite_ponderacao: 1.15,
  score_peso_error: 15,
  score_peso_warning: 3,
};

export function calcScoreConformidade(
  erros: number,
  avisos: number,
  thresholds: ConformidadeThresholds = DEFAULT_THRESHOLDS,
): number {
  return Math.max(0, 100 - (erros * thresholds.score_peso_error) - (avisos * thresholds.score_peso_warning));
}

export function getImpactoOperacional(resultado: ConformidadeResultado): ImpactoOperacional {
  return (resultado.dados_contexto?.impacto_operacional as ImpactoOperacional) ?? 'informativo';
}

// ═══ Hooks ═══

export function useConformidadeResultados(contratoId: string, osId?: string) {
  return useQuery({
    queryKey: ['emp-conformidade-resultados', contratoId, osId],
    queryFn: async () => {
      let query = supabase
        .from('emp_conformidade_resultados' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .order('severidade', { ascending: true })
        .order('regra', { ascending: true });

      if (osId) {
        query = query.eq('os_id', osId);
      } else {
        query = query.is('os_id', null);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as ConformidadeResultado[];
    },
    enabled: !!contratoId,
  });
}

export function useConformidadeExecucoes(contratoId: string, osId?: string) {
  return useQuery({
    queryKey: ['emp-conformidade-execucoes', contratoId, osId],
    queryFn: async () => {
      let query = supabase
        .from('emp_conformidade_execucoes' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (osId) {
        query = query.eq('os_id', osId);
      } else {
        query = query.is('os_id', null);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as ConformidadeExecucao[];
    },
    enabled: !!contratoId,
  });
}

export function useConformidadeGuardaChuva(contratoId: string) {
  return useQuery({
    queryKey: ['emp-conformidade-guarda-chuva', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_vw_conformidade_guarda_chuva' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as ConformidadeGuardaChuva | null;
    },
    enabled: !!contratoId,
  });
}

export function useValidarConformidade() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      contrato_id: string;
      trigger_type: 'auto_planilha' | 'auto_cronograma' | 'manual';
      os_id?: string;
    }) => {
      const resp = await supabase.functions.invoke('validar-conformidade', {
        body: params,
      });
      if (resp.error) throw new Error(resp.error.message || 'Erro na validação');
      return resp.data as {
        success: boolean;
        score_conformidade: number;
        total_erros: number;
        total_avisos: number;
        total_info: number;
        thresholds_aplicados: ConformidadeThresholds;
        per_os_scores?: Array<{
          os_id: string;
          score: number;
          erros: number;
          avisos: number;
          info: number;
          bac: number;
        }>;
        resultados: Array<{
          regra: string;
          severidade: string;
          impacto_operacional: ImpactoOperacional;
          entidade_afetada: string;
          descricao: string;
          sugestao_correcao: string;
        }>;
      };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['emp-conformidade-resultados', variables.contrato_id] });
      queryClient.invalidateQueries({ queryKey: ['emp-conformidade-execucoes', variables.contrato_id] });
      queryClient.invalidateQueries({ queryKey: ['emp-conformidade-guarda-chuva', variables.contrato_id] });

      if (data.total_erros === 0 && data.total_avisos === 0) {
        toast({ title: 'Conformidade OK', description: `Score: ${data.score_conformidade}% — nenhum problema encontrado.` });
      } else {
        toast({
          title: `Conformidade: ${data.total_erros} erro(s), ${data.total_avisos} aviso(s)`,
          description: `Score: ${data.score_conformidade}%`,
          variant: data.total_erros > 0 ? 'destructive' : undefined,
        });
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Erro na validação cruzada', description: err.message, variant: 'destructive' });
    },
  });
}
